import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import {
  getArticlesFilterList,
  deleteArticle,
  changeArticleStatus,
} from "../../../services/articlesService";
import { getSectionsList } from "../../../services/sectionsService";
import TableHeader from "../../Common/TableComponent/TableHeader";
import EntriesDropdown from "../../Common/TableComponent/EntriesDropdown";
import TablesRow from "../../Common/TableComponent/TablesRow";
import { Pagination } from "../../Common/TableComponent/Pagination";
import TableSkeleton from "../../Common/Loaders/TableSkeleton";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { useUserProfile } from "../../../hooks/useUserProfile";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import allImages from "../../../assets/images-import";
import { ARTICLE_STATUS } from "./articleConstants";
import {
  resolveArticleStatus,
  renderArticleStatusBadge,
} from "./articleStatusUtils";
import "./manage-articles.css";

const ARTICLE_PUBLIC_SITE_BASE_URL = "https://www.xcruitenergy.com";

const DATE_ONLY_PICKER_OPTIONS = {
  dateFormat: "Y-m-d",
  monthSelectorType: "static",
  appendTo: typeof document !== "undefined" ? document.body : undefined,
};

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "Active", label: "Active" },
  { value: "Draft", label: "Draft" },
  { value: "Inactive", label: "Inactive" },
];

const mapSectionFilterOptions = (list) =>
  (list || []).map((item) => ({
    value: String(item.id ?? item.Id ?? ""),
    label: item.sectionName || item.SectionName || "-",
  }));

const getArticleId = (item) => item.id ?? item.Id;

const getTitle = (item) => item.title || item.Title || "-";

const getContentSlug = (item) => item.contentSlug || item.ContentSlug || "";

const getImageUrl = (item) =>
  item.contentImageUrl ||
  item.ContentImageUrl ||
  item.bannerImageUrl ||
  item.BannerImageUrl ||
  allImages.DefultImage;

// Existing project date formatter (day + short month + year, e.g. "10 Jun 2026").
const formatShortDate = (raw) => {
  if (!raw) return "-";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const getPublishedOnRaw = (item) =>
  item.publishedOn || item.PublishedOn || null;

const getPublishedOn = (item) => formatShortDate(getPublishedOnRaw(item));

const getAddedOnRaw = (item) => item.addedOn ?? item.AddedOn ?? null;

const getAddedOn = (item) => formatShortDate(getAddedOnRaw(item));

const toTimestamp = (raw) => {
  if (!raw) return null;
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? null : time;
};

// Missing dates always sort last, regardless of direction.
const compareTimestamps = (ta, tb, direction) => {
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;
  return direction === "asc" ? ta - tb : tb - ta;
};

const SORT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "title_asc", label: "Article Name (A–Z)" },
  { value: "title_desc", label: "Article Name (Z–A)" },
  { value: "published_desc", label: "Newest Published" },
  { value: "published_asc", label: "Oldest Published" },
  { value: "added_desc", label: "Newest Added" },
  { value: "added_asc", label: "Oldest Added" },
];

const renderThumbnail = (url) => (
  <img
    src={url || allImages.DefultImage}
    alt="Article"
    className="rounded xc-thumb"
    style={{ width: 48, height: 48, objectFit: "cover" }}
    onError={(e) => {
      e.target.src = allImages.DefultImage;
    }}
  />
);

const renderArticleCell = (item) => {
  const slug = getContentSlug(item);
  const id = getArticleId(item);
  const href = slug
    ? `${ARTICLE_PUBLIC_SITE_BASE_URL}/article/${slug}-${id}?preview=yes`
    : "#";
  return (
    <div className="d-flex align-items-center gap-3">
      {renderThumbnail(getImageUrl(item))}
      <div className="d-flex flex-column gap-1">
        <div className="d-flex align-items-center gap-2">
          <i className="ri-links-line xc-meta-icon"></i>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="xc-cell-link"
          >
            <span className="xc-article-title">{getTitle(item)}</span>
          </a>
        </div>
        <div className="d-flex align-items-center gap-1 xc-article-meta">
          <i className="ri-calendar-event-line xc-meta-icon"></i>
          <span>Added on • {getAddedOn(item)}</span>
        </div>
      </div>
    </div>
  );
};

export const ManageArticles = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "articles";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const { isSuperAdmin } = useUserProfile();
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [articlesList, setArticlesList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusChangingId, setStatusChangingId] = useState(null);
  const [sections, setSections] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState("default");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    searchText: "",
    sectionId: "",
    publishedFrom: null,
    publishedTo: null,
    status: "",
  });

  const hasActiveFilters = Boolean(
    filters.sectionId ||
      filters.publishedFrom ||
      filters.publishedTo ||
      filters.status ||
      (sortBy && sortBy !== "default")
  );

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  useEffect(() => {
    const loadSections = async () => {
      try {
        const response = await getSectionsList();
        const list = response.data?.result || response.data || [];
        setSections(mapSectionFilterOptions(Array.isArray(list) ? list : []));
      } catch (error) {
        handleErrors(error);
      }
    };
    loadSections();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const payload = {
        pageNumber: currentPage,
        pageSize: entriesPerPage,
        searchText: filters.searchText || "",
        sectionId: filters.sectionId || null,
        publishedFrom: filters.publishedFrom || null,
        publishedTo: filters.publishedTo || null,
        status: filters.status || "",
      };
      const response = await getArticlesFilterList(payload);
      const result = response.data?.result || {};
      const items = Array.isArray(result.items) ? result.items : [];
      setArticlesList(items);
      setTotalCount(result.totalCount || 0);
    } catch (error) {
      handleErrors(error);
      setArticlesList([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, entriesPerPage, filters]);

  const handleSearchClick = () => {
    setFilters((prev) => ({ ...prev, searchText: searchInput }));
    setCurrentPage(1);
  };

  const handleSectionFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, sectionId: e.target.value }));
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, status: e.target.value }));
    setCurrentPage(1);
  };

  const handlePublishedFromChange = (date) => {
    setFilters((prev) => ({ ...prev, publishedFrom: date || null }));
    setCurrentPage(1);
  };

  const handlePublishedToChange = (date) => {
    setFilters((prev) => ({ ...prev, publishedTo: date || null }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSortBy("default");
    setFilters({
      searchText: "",
      sectionId: "",
      publishedFrom: null,
      publishedTo: null,
      status: "",
    });
    setCurrentPage(1);
  };

  const handlePublishToggle = async (item, checked) => {
    const articleId = getArticleId(item);
    const currentStatus = resolveArticleStatus(item);

    // Determine desired status based on toggle state and rules:
    // - Turning ON => Active
    // - Turning OFF => Inactive (unless current is Draft -> remain Draft)
    let targetStatus = null;
    if (checked) {
      targetStatus = ARTICLE_STATUS.ACTIVE;
    } else {
      if (currentStatus === ARTICLE_STATUS.DRAFT) {
        // Leave as Draft; do not convert to Inactive
        return;
      }
      targetStatus = ARTICLE_STATUS.INACTIVE;
    }

    setStatusChangingId(articleId);
    try {
      const response = await changeArticleStatus({
        id: articleId,
        status: targetStatus,
      });

      if (response?.data?.isSuccess === false) {
        toast.error(
          response.data.message || "Failed to update article status.",
        );
        return;
      }

      setArticlesList((prev) =>
        prev.map((row) =>
          getArticleId(row) === articleId
            ? {
                ...row,
                status: targetStatus,
                Status: targetStatus,
                isPublished: targetStatus === ARTICLE_STATUS.ACTIVE,
              }
            : row,
        ),
      );

      toast.success(
        targetStatus === ARTICLE_STATUS.ACTIVE
          ? "Article published successfully!"
          : "Article unpublished successfully!",
      );

      fetchData();
    } catch (error) {
      handleErrors(error);
    } finally {
      setStatusChangingId(null);
    }
  };

  const renderPublishToggle = (item) => {
    const articleId = getArticleId(item);
    const isActive = Boolean(item.isPublished);
    const isUpdating = statusChangingId === articleId;

    return (
      <div className="d-flex align-items-center">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`article-switch-${articleId}`}
            checked={isActive}
            onChange={(e) => handlePublishToggle(item, e.target.checked)}
            disabled={!pageAccessDetails.editAccess || isUpdating}
          />
        </div>
        {isUpdating ? (
          <div
            className="spinner-border spinner-border-sm ms-2"
            role="status"
          />
        ) : null}
      </div>
    );
  };

  const sortedArticles = useMemo(() => {
    if (sortBy === "default") {
      return articlesList;
    }

    const list = [...articlesList];

    switch (sortBy) {
      case "title_asc":
        list.sort((a, b) => getTitle(a).localeCompare(getTitle(b)));
        break;
      case "title_desc":
        list.sort((a, b) => getTitle(b).localeCompare(getTitle(a)));
        break;
      case "published_desc":
        list.sort((a, b) =>
          compareTimestamps(
            toTimestamp(getPublishedOnRaw(a)),
            toTimestamp(getPublishedOnRaw(b)),
            "desc",
          ),
        );
        break;
      case "published_asc":
        list.sort((a, b) =>
          compareTimestamps(
            toTimestamp(getPublishedOnRaw(a)),
            toTimestamp(getPublishedOnRaw(b)),
            "asc",
          ),
        );
        break;
      case "added_desc":
        list.sort((a, b) =>
          compareTimestamps(
            toTimestamp(getAddedOnRaw(a)),
            toTimestamp(getAddedOnRaw(b)),
            "desc",
          ),
        );
        break;
      case "added_asc":
        list.sort((a, b) =>
          compareTimestamps(
            toTimestamp(getAddedOnRaw(a)),
            toTimestamp(getAddedOnRaw(b)),
            "asc",
          ),
        );
        break;
      default:
        break;
    }

    return list;
  }, [articlesList, sortBy]);

  const totalPages = Math.ceil(totalCount / entriesPerPage) || 0;

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (articleId) => {
    const confirmed = await confirmDelete("Article");
    if (confirmed) {
      try {
        await deleteArticle(articleId);
        Swal.fire(
          "Deleted!",
          "The article has been deleted successfully.",
          "success",
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  const tableColumns = useMemo(() => {
    const columns = ["Title", "Published Date", "Status"];
    if (isSuperAdmin) {
      columns.push("Publish");
    }
    columns.push("Action");
    return columns;
  }, [isSuperAdmin]);

  const rowColumns = useMemo(() => {
    const columns = ["title", "publishedDate", "status"];
    if (isSuperAdmin) {
      columns.push("publishToggle");
    }
    return columns;
  }, [isSuperAdmin]);

  return (
    <>
      <ComponentHeader title="Articles" />

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5 xc-manage-articles">
              <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h5 className="mb-0">Manage Articles</h5>
                {pageAccessDetails.addAccess ? (
                  <Link to="/articles/add" className="btn btn-secondary btn-sm">
                    <i className="ri-add-line align-middle me-1"></i>
                    Add Article
                  </Link>
                ) : null}
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <div className="xc-toolbar">
                  <EntriesDropdown
                    entriesPerPage={entriesPerPage}
                    onEntriesChange={handleEntriesChange}
                  />
                  <div className="xc-toolbar__right">
                    <div className="xc-table-search xc-quick-search">
                      <i
                        className="ri-search-line xc-table-search__icon xc-quick-search-icon"
                        onClick={handleSearchClick}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSearchClick();
                          }
                        }}
                        aria-label="Search"
                      ></i>
                      <input
                        type="search"
                        id="articleSearchText"
                        className="form-control xc-table-search__input"
                        placeholder="Search articles..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearchClick();
                          }
                        }}
                        autoComplete="off"
                      />
                    </div>
                    <div className="xc-filter-popup">
                      <button
                        type="button"
                        className={`xc-filter-icon-btn ${
                          isFilterOpen ? "is-active" : ""
                        } ${hasActiveFilters ? "has-filters" : ""}`}
                        onClick={() => setIsFilterOpen(true)}
                        aria-expanded={isFilterOpen}
                        aria-haspopup="dialog"
                        title="Filter"
                        aria-label="Filter"
                      >
                        <i className="ri-filter-2-fill" aria-hidden="true" />
                        {hasActiveFilters ? (
                          <span className="xc-filter-icon-btn__dot" />
                        ) : null}
                      </button>
                    </div>
                  </div>
                </div>

                <Modal
                  show={isFilterOpen}
                  onHide={() => setIsFilterOpen(false)}
                  centered
                  className="xc-filter-modal"
                  dialogClassName="xc-filter-modal__dialog"
                  contentClassName="xc-filter-modal__content"
                  enforceFocus={false}
                >
                  <Modal.Header closeButton className="xc-filter-modal__header">
                    <div>
                      <Modal.Title as="h5">Filter articles</Modal.Title>
                      <p className="xc-filter-modal__subtitle mb-0">
                        Refine articles by section, publish dates, status, or
                        sort order.
                      </p>
                    </div>
                  </Modal.Header>
                  <Modal.Body className="xc-filter-modal__body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label
                          htmlFor="articleSectionFilter"
                          className="form-label"
                        >
                          Section
                        </label>
                        <select
                          id="articleSectionFilter"
                          className="form-select"
                          value={filters.sectionId}
                          onChange={handleSectionFilterChange}
                        >
                          <option value="">All Sections</option>
                          {sections.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label
                          htmlFor="articleStatusFilter"
                          className="form-label"
                        >
                          Status
                        </label>
                        <select
                          id="articleStatusFilter"
                          className="form-select"
                          value={filters.status}
                          onChange={handleStatusFilterChange}
                        >
                          {STATUS_FILTER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label
                          htmlFor="articlePublishedFrom"
                          className="form-label"
                        >
                          Published From
                        </label>
                        <Flatpickr
                          id="articlePublishedFrom"
                          className="form-control"
                          placeholder="Select date"
                          value={filters.publishedFrom}
                          options={DATE_ONLY_PICKER_OPTIONS}
                          onChange={([date]) =>
                            handlePublishedFromChange(date)
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label
                          htmlFor="articlePublishedTo"
                          className="form-label"
                        >
                          Published To
                        </label>
                        <Flatpickr
                          id="articlePublishedTo"
                          className="form-control"
                          placeholder="Select date"
                          value={filters.publishedTo}
                          options={DATE_ONLY_PICKER_OPTIONS}
                          onChange={([date]) => handlePublishedToChange(date)}
                        />
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="articleSortBy" className="form-label">
                          Sort By
                        </label>
                        <select
                          id="articleSortBy"
                          className="form-select"
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                        >
                          {SORT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </Modal.Body>
                  <Modal.Footer className="xc-filter-modal__footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleClearFilters}
                    >
                      Clear
                    </button>
                    <div className="xc-filter-modal__footer-right">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setIsFilterOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setIsFilterOpen(false)}
                      >
                        Apply filters
                      </button>
                    </div>
                  </Modal.Footer>
                </Modal>

                {loading ? (
                  <TableSkeleton
                    columns={tableColumns}
                    rows={entriesPerPage > 10 ? 10 : entriesPerPage}
                  />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered xc-manage-articles-table">
                      <TableHeader columns={tableColumns} />
                      <tbody className="manage-page-group-table-values">
                        {sortedArticles.length === 0 ? (
                          <TableDataStatusError
                            colspan={String(tableColumns.length)}
                          />
                        ) : (
                          sortedArticles.map((item) => {
                            const status = resolveArticleStatus(item);
                            const rowData = {
                              title: renderArticleCell(item),
                              publishedDate: getPublishedOn(item),
                              status: renderArticleStatusBadge(status),
                            };

                            if (isSuperAdmin) {
                              rowData.publishToggle = renderPublishToggle(item);
                            }

                            return (
                              <TablesRow
                                key={getArticleId(item)}
                                rowData={rowData}
                                columns={rowColumns}
                                hideIcons={false}
                                onEdit={() => {
                                  navigate(
                                    `/articles/update/${getArticleId(item)}`,
                                  );
                                }}
                                onDelete={() =>
                                  handleDelete(getArticleId(item))
                                }
                                pageLevelAccessData={pageAccessDetails}
                              />
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={totalCount}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};
